<template>
  <section>
    <div class="photos-row" @click="openFullscreen()">
      <div
        v-for="(photo, index) in loadedPhotos.slice(0, 3)"
        :key="index"
        :class="{ last: index === 2 }"
        class="photo-wrapper"
      >
        <img
          v-if="$isDesktop"
          :loading="useLazyImages ? 'lazy' : 'eager'"
          :src="`/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=264,h=198${photo.url.masterImage}`"
          :srcset="`
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=264,h=198${photo.url.masterImage} 264w,
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=70,w=360,h=270${photo.url.masterImage} 360w,
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=50,w=720,h=540${photo.url.masterImage} 720w
          `"
          :alt="capitalize(photoCaptionAltFormatter(photo.caption))"
          sizes="264px"
          width="264"
          height="198"
          @imageLoadingError="handlePhotoLoadingError"
        />
        <img
          v-else
          :loading="useLazyImages ? 'lazy' : 'eager'"
          :src="`/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=264,h=198${photo.url.masterImage}`"
          :srcset="`
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=264,h=198${photo.url.masterImage} 264w,
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=70,w=360,h=270${photo.url.masterImage} 360w,
            /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=50,w=720,h=540${photo.url.masterImage} 720w
          `"
          :alt="capitalize(photoCaptionAltFormatter(photo.caption))"
          sizes="33vw"
          width="142"
          height="107"
          @imageLoadingError="handlePhotoLoadingError"
        />
        <template v-if="index == 0">
          <div class="photo-count-fullscreen">
            <img
              src="@/assets/images/photo-icon-white.svg"
              width="17"
              height="17"
              alt=""
              class="photograph icon"
            />
            <i class="photo-count">{{ loadedPhotos.length }}</i>
            <img
              src="@/assets/images/fullscreen.svg"
              width="16"
              height="16"
              alt=""
              class="fullscreen icon"
            />
          </div>
        </template>
      </div>
    </div>

    <client-only>
      <div class="inline-gallery pt-gallery has-fullscreen">
        <PuFlickity
          ref="ptflickity"
          :options="flickityOptions"
          @init="initGallery"
        >
          <div
            v-for="(photo, index) in loadedPhotos"
            :key="'photo-' + index"
            class="pt-carousel-cell carousel-cell"
          >
            <img
              loading="lazy"
              :src="photo.url.galleryMedium"
              :srcset="
                $isDesktop && isFullScreen
                  ? `/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=720,h=540${photo.url.masterImage} 720w, /_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=1080,h=810${photo.url.masterImage} 1080w, /_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=1800,h=1350${photo.url.masterImage} 1800w`
                  : `/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=720,h=540${photo.url.masterImage} 720w, /_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=1080,h=810${photo.url.masterImage} 1080w`
              "
              :data-caption="
                $i18n.locale.split('-')[0] === 'en'
                  ? capitalize(photo.caption)
                  : capitalize(photo.titleAttribution)
              "
              :alt="capitalize(photoCaptionAltFormatter(photo.caption))"
            />
          </div>
          <i class="photo-count photo-count-fs"
            >{{ currentImage }}/{{ loadedPhotos.length }}</i
          >
          <p class="caption">
            <!-- image caption written in here -->
          </p>
        </PuFlickity>
      </div>
    </client-only>
  </section>
</template>

<script lang="ts">
import type { Route } from 'vue-router'
import { ignoreTouchesWhenSwiping } from '~/utility/flickity'
import { photoCaptionAltFormatter } from '~/utility/photoCaptionAltFormatter'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { capitalize } from '~/utility/text'
import type PuFlickity from '../../../components/PuFlickity.vue'
import { PitchTypePhoto } from '../../campsite-details/types'

@Component({
  emits: [],
})
class PitchTypePhotos extends Vue {
  declare $refs: { ptflickity: InstanceType<typeof PuFlickity> }

  @Prop({ required: true })
  photos: PitchTypePhoto[]

  @Prop({ required: true })
  fullscreenModalHash: string

  @Prop({ default: true })
  useLazyImages: boolean

  currentImage: number = 1
  loadedPhotos: PitchTypePhoto[] = []

  get flickityOptions() {
    return {
      initialIndex: this.currentImage - 1,
      dragThreshold: 20,
      pageDots: false,
      wrapAround: true,
      lazyLoad: 1,
      cellSelector: '.pt-carousel-cell',
      selectedAttraction: 0.2,
      friction: 0.8,
      fullscreen: true,
      prevNextButtons: this.$isDesktop,
      draggable: !this.$isDesktop,
    }
  }

  gallery: any = null
  caption: any = null
  isFullScreen: any = false
  selectedImage: any = null
  exitFullscreenButton: HTMLElement | null = null
  flickityInstance: any = null

  mounted() {
    this.loadedPhotos = this.photos
  }

  initGallery(flickityInstance) {
    this.flickityInstance = flickityInstance
    this.gallery = this.$el
    this.caption = this.gallery.querySelector('.caption')
    this.initHandlers()
    if (this.$route.hash === `#${this.fullscreenModalHash}`) {
      this.flickityInstance?.viewFullscreen()
    }
    this.exitFullscreenButton =
      this.flickityInstance?.exitFullscreenButton?.element
  }

  initHandlers() {
    const f = this.$refs.ptflickity
    f.on('change', () => this.updateSlideStatus())
    f.on('select', () => this.updateCaption())
    f.on(
      'fullscreenChange',
      async (fullscreenStatus: boolean) =>
        await this.fullscreenChange(fullscreenStatus),
    )
    ignoreTouchesWhenSwiping(this.gallery)
  }

  @Watch('$route')
  routeChange(to: Route, from?: Route) {
    if (this.$.isUnmounted) return

    if (to.path === from?.path) {
      to.meta!.scroll = false
    }
    if (to.hash === `#${this.fullscreenModalHash}`) {
      this.flickityInstance?.viewFullscreen?.()
    } else {
      this.flickityInstance?.exitFullscreen?.()
    }
  }

  handlePhotoLoadingError(failedToLoadPhotoSrc: string) {
    this.loadedPhotos = this.photos.filter(
      (photo) => photo.url.detailSmall !== failedToLoadPhotoSrc,
    )
  }

  async openFullscreen() {
    await this.$router.push({
      hash: `#${this.fullscreenModalHash}`,
      query: this.$router.currentRoute.value.query,
    })
  }

  async closeFullscreen() {
    this.$routerClient.getReferrer()
      ? await this.$router.back()
      : await this.$router.push({
          query: this.$router.currentRoute.value.query,
        })
  }

  async fullscreenChange(fullscreenStatus: boolean) {
    if (this.$.isUnmounted) return

    this.isFullScreen = fullscreenStatus
    await this.$nextTick()

    if (this.$.isUnmounted) return

    this.reactivateFlickity()
    this.updateCaption()
    this.updateSlideStatus()

    if (!this.$.isUnmounted) {
      this.isFullScreen
        ? this.gallery.setAttribute('gallery-open', 'true')
        : this.gallery.removeAttribute('gallery-open')
      this.exitFullscreenButton?.addEventListener('click', this.closeFullscreen)
    }
  }

  reactivateFlickity() {
    if (this.isFullScreen && !this.$.isUnmounted) {
      document.documentElement.classList.add('is-flickity-fullscreen')
    }
  }

  updateSlideStatus() {
    if (this.$.isUnmounted || !this.$refs.ptflickity) return

    const slideIndex = this.$refs.ptflickity.selectedIndex() + 1
    this.currentImage = slideIndex
  }

  updateCaption() {
    if (this.$.isUnmounted || !this.isFullScreen || !this.gallery) return

    this.selectedImage = this.gallery.querySelector(
      '.pt-carousel-cell.is-selected img',
    )
    if (this.selectedImage) {
      this.caption.textContent = this.selectedImage.getAttribute('data-caption')
    }
  }

  capitalize(value: string) {
    return capitalize(value)
  }

  photoCaptionAltFormatter(caption) {
    return photoCaptionAltFormatter(caption)
  }

  // workaround for iOS where a child element with position: fixed will use a transformed parent element for its fixed position
  @Watch('isFullScreen')
  changeBodyAttribute(value: boolean) {
    if (this.$.isUnmounted) return

    value
      ? document.body.setAttribute('data-flickity-fullscreen', 'true')
      : document.body.removeAttribute('data-flickity-fullscreen')
  }

  beforeUnmount() {
    if (this && document && document.body) {
      document.body.removeAttribute('data-flickity-fullscreen')
    }
    if (this && document && document.documentElement) {
      document.documentElement.classList.remove('is-flickity-fullscreen')
    }
  }
}

export default toNative(PitchTypePhotos)
</script>

<style>
@import url('~/assets/css/flickity-custom.css');
</style>

<style scoped>
.photos-row {
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  min-height: 24.75vw;
  background-color: #eef2f7;
}

.photo-wrapper {
  position: relative;
  max-width: calc(100% / 3);
  min-height: 24.75vw;
  border-right: 1px solid white;
}

.photo-wrapper.last {
  border: none;
}

.photo-wrapper > img {
  width: 100%;
  max-width: none;
}

@media screen and (min-width: 320px) and (max-width: 998px) and (orientation: landscape) {
  .photo-wrapper > img {
    width: auto;
  }
}

.photo-count-fullscreen {
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 0.25em;
  padding: 0 0.35em 0.25em;
}

.photo-count {
  font-size: var(--body);
  color: white;
  text-shadow: 0 1px 1px rgb(0 0 0 / 40%);
}

.photos-row .photo-count-fullscreen img {
  border-radius: 0 !important;
  filter: drop-shadow(0 1px 1px rgb(0 0 0 / 40%));
}

.fullscreen.icon {
  margin: 0 0 0 auto;
}

.pt-gallery {
  /* move offscreen until needed */
  position: absolute;
  left: -100vw;
}
</style>
